import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Glasses, Crown, Flower2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import UnlockPreviewCard from "../UnlockPreviewCard";
import Avatar from "../Avatar";

import { thresholdsOf, isBetaPace } from "@/lib/unlockPace";
import { pathLine } from "@/lib/unlocks";
import BirthdayPicker from "./BirthdayPicker";

const BAR_INITIAL = { width: 0 };
const BAR_TRANSITION = { duration: 0.6 };

// Stage + unlock ladders are built from the user's effective thresholds
// (normal, or the compressed beta ladder the owner can flip on in /admin).
export const stagesFor = (t) => [
    { id: "new",        label: "Shy & curious",  emoji: "🌱", min: 0,                    next: t.stages.warming,    blurb: "Mochi is still warming up to you. Soft hi's, polite vibes." },
    { id: "warming",    label: "Warming up",     emoji: "🍡", min: t.stages.warming,    next: t.stages.friendly,   blurb: "Small jokes start. Mochi teases (gently)." },
    { id: "friendly",   label: "Friendly",       emoji: "✨", min: t.stages.friendly,   next: t.stages.old_friend, blurb: "Loving roasts. Mochi references your past wins." },
    { id: "old_friend", label: "Old friend",     emoji: "💛", min: t.stages.old_friend, next: null,                blurb: "Inside jokes. Mochi feels like an old friend." },
];

// Wearables (pin / crown / hat) read their multi-path progress from the
// backend-computed `settings.unlock_progress` — "whichever comes first".
const wearable = (id, label, icon, preview) => ({
    id, label, icon, preview, kind: "wearable",
    req: (s) => !!s.unlock_progress?.[id]?.unlocked,
    paths: (s) => s.unlock_progress?.[id]?.paths || [],
});

const unlocksFor = (t) => [
    { id: "sunglasses", label: "Sunglasses (in spirit)", icon: "🕶️", req: (s) => (s.pomodoros_completed || 0) >= t.sunglasses, current: (s) => s.pomodoros_completed || 0, target: t.sunglasses, unit: "Pomodoros", requirement: `Finish ${t.sunglasses} Pomodoro focus sessions`, preview: { skin: "sunglasses" } },
    wearable("pin",   "Sakura pin",   "🌸", { pin: true }),
    wearable("crown", "Gold crown",   "👑", { accessory: "crown" }),
    wearable("hat",   "Birthday hat", "🎂", { accessory: "birthday-hat" }),
    { id: "warming",    label: "Mochi teases you",        icon: "🍡", req: (s) => ["warming","friendly","old_friend"].includes(s.relationship_stage), current: (s) => s._days_known || 0, target: t.stages.warming, unit: "days known", requirement: `Spend ${t.stages.warming} days with Mochi` },
    { id: "friendly",   label: "Loving roasts unlocked",  icon: "✨", req: (s) => ["friendly","old_friend"].includes(s.relationship_stage), current: (s) => s._days_known || 0, target: t.stages.friendly, unit: "days known", requirement: `Spend ${t.stages.friendly} days with Mochi` },
    { id: "old_friend", label: "Old-friend status",       icon: "💛", req: (s) => s.relationship_stage === "old_friend", current: (s) => s._days_known || 0, target: t.stages.old_friend, unit: "days known", requirement: `Spend ${t.stages.old_friend} days with Mochi` },
];

// Compact "whichever comes first" progress list for a wearable card.
function PathList({ paths, testId }) {
    return (
        <ul className="mt-1 space-y-0.5" data-testid={testId}>
            {paths.map((p) => (
                <li key={p.id} className={`font-body text-[10px] leading-snug ${p.met ? "text-sage" : "text-warm-500"}`}>
                    {p.met ? "✓" : "·"} {pathLine(p)}
                </li>
            ))}
        </ul>
    );
}

// A reusable on/off pill switch row (sunglasses / crown / birthday hat).
function ToggleRow({ icon, label, onLine, offLine, on, onToggle, color = "terracotta", testId }) {
    return (
        <button
            onClick={onToggle}
            className="mt-3 w-full flex items-center gap-3 rounded-2xl bg-cream/90 px-3 py-2.5 text-left transition-all hover:bg-cream"
            data-testid={testId}
            aria-pressed={on}
        >
            {icon}
            <div className="flex-1 min-w-0">
                <p className="font-display text-sm text-warm-900 leading-tight">{label}</p>
                <p className="font-body text-[11px] text-warm-500 mt-0.5">{on ? onLine : offLine}</p>
            </div>
            <span
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                    on ? (color === "mustard" ? "bg-mustard" : "bg-terracotta") : "bg-warm-500/30"
                }`}
                data-testid={`${testId.replace("-toggle", "")}-switch`}
            >
                <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-cream shadow-clay transition-transform ${
                        on ? "translate-x-5" : "translate-x-0.5"
                    }`}
                />
            </span>
        </button>
    );
}

// "Mochi & you" — relationship progression card with stage progress bar,
// unlock grid, accessory equip toggles, and the cover-pass indicator.
export default function MochiAndYouCard({ settings, onUpdate, daysKnown, isAdmin = false, onUnlockAll }) {
    const thresholds = thresholdsOf(settings);
    const STAGES = stagesFor(thresholds);
    const UNLOCKS = unlocksFor(thresholds);
    const stage = STAGES.find((s) => s.id === settings.relationship_stage) || STAGES[0];
    const nextStage = stage.next ? STAGES.find((s) => s.min === stage.next) : null;
    const progressToNext = stage.next
        ? Math.min(1, Math.max(0, (daysKnown - stage.min) / (stage.next - stage.min)))
        : 1;
    const settingsWithDays = { ...settings, _days_known: daysKnown };

    const sunglassesUnlocked = (settings.pomodoros_completed || 0) >= thresholds.sunglasses;
    const sunglassesEquipped = settings.sunglasses_equipped ?? true;
    const crownEquipped = settings.crown_equipped !== false;
    const hatUntil = settings.milestone_365_hat_until;
    const hatActive = hatUntil && Date.parse(hatUntil) > Date.now();
    const hatPermanent = !!settings.hat_equipped;
    const hatVisible = settings.accessory_visible !== false;
    const pinUnlocked = !!settings.pin_unlocked || !!settings.pin_equipped;
    const crownUnlocked = !!settings.crown_unlocked;
    const hatUnlocked = !!settings.hat_unlocked;
    const hatOccasion = settings.unlock_progress?.hat?.occasion || null;
    const pinEquipped = !!settings.pin_equipped;
    const coverEverUsed = (settings.cover_pass_used_count || 0) > 0;
    const coverAvailable = settings.cover_pass_available !== false;

    const [unlocking, setUnlocking] = React.useState(false);
    const handleUnlockAll = async () => {
        if (!onUnlockAll || unlocking) return;
        setUnlocking(true);
        try {
            await onUnlockAll();
        } catch {
            toast.error("Couldn't unlock everything — try again?");
        } finally {
            setUnlocking(false);
        }
    };

    return (
        <div className="mb-5 rounded-clay bg-gradient-to-br from-terracotta-soft/60 to-mustard-soft/40 p-4" data-testid="mochi-and-you">
            <div className="flex items-start gap-3 mb-3">
                <Avatar mood="happy" size={56} style={settings.avatar_style || "feminine"} skin={sunglassesUnlocked && sunglassesEquipped ? "sunglasses" : null} />
                <div className="flex-1 min-w-0">
                    <p className="font-display text-xs uppercase tracking-widest text-warm-500">Mochi & you</p>
                    <p className="font-display text-lg text-warm-900 leading-tight" data-testid="mochi-and-you-headline">
                        {daysKnown === 0
                            ? "Today we meet!"
                            : daysKnown === 1
                            ? "1 day together"
                            : `${daysKnown} days together`}
                    </p>
                    <p className="font-body text-xs text-warm-500 italic mt-0.5">{stage.blurb}</p>
                    {isBetaPace(settings) && (
                        <span className="inline-flex items-center gap-1 mt-1.5 rounded-full bg-mustard-soft px-2 py-0.5 font-display text-[10px] text-warm-900" data-testid="mochi-beta-pace-pill">
                            ✦ beta pace — unlocks arrive faster
                        </span>
                    )}
                </div>
                <span
                    className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-display bg-cream/80 text-warm-900 rounded-full px-2 py-1"
                    data-testid="mochi-stage-pill"
                >
                    {stage.emoji} {stage.label}
                </span>
            </div>

            {nextStage ? (
                <>
                    <div className="flex items-center justify-between text-[11px] font-body text-warm-500 mb-1">
                        <span>now · {stage.label}</span>
                        <span>next · {nextStage.label} {nextStage.emoji}</span>
                    </div>
                    <div className="h-2 rounded-full bg-cream overflow-hidden">
                        <motion.div
                            initial={BAR_INITIAL}
                            animate={{ width: `${Math.round(progressToNext * 100)}%` }}
                            transition={BAR_TRANSITION}
                            className="h-full bg-terracotta rounded-full"
                            data-testid="mochi-stage-bar"
                        />
                    </div>
                    <p className="text-[11px] font-body text-warm-500 mt-1.5 text-right">
                        {Math.max(0, stage.next - daysKnown)} {stage.next - daysKnown === 1 ? "day" : "days"} until {nextStage.label.toLowerCase()}
                    </p>
                </>
            ) : (
                <p className="text-xs font-body text-warm-500 text-center italic">
                    You and Mochi go way back. 💛
                </p>
            )}

            {/* Unlocks */}
            <p className="font-display text-xs uppercase tracking-widest text-warm-500 mt-4 mb-2 flex items-center gap-1">
                <Sparkles size={12} /> Unlocks
            </p>
            <div className="grid grid-cols-2 gap-2" data-testid="mochi-unlocks">
                {UNLOCKS.map((u) => {
                    const got = u.req(settingsWithDays);
                    const curr = u.current ? u.current(settingsWithDays) : 0;
                    return (
                        <UnlockPreviewCard
                            key={u.id}
                            preview={u.preview}
                            locked={!got}
                            avatarStyle={settings.avatar_style || "feminine"}
                            testId={`mochi-unlock-${u.id}`}
                        >
                            <div
                                className={`rounded-2xl p-2.5 transition-all select-none ${got ? "bg-cream/90" : "bg-cream/40"} ${u.preview && got ? "cursor-pointer" : ""}`}
                            >
                                <div className="flex items-center gap-1.5">
                                    <span className={`text-lg ${got ? "" : "opacity-40 grayscale"}`}>{u.icon}</span>
                                    <p className={`font-display text-xs leading-tight ${got ? "text-warm-900" : "text-warm-500"}`}>{u.label}</p>
                                </div>
                                {got ? (
                                    <p className="font-body text-[10px] mt-1 text-sage" data-testid={`mochi-unlock-${u.id}-status`}>
                                        {u.preview ? "Hold to try on ✨" : "Unlocked ✓"}
                                    </p>
                                ) : (
                                    <div className="mt-1" data-testid={`mochi-unlock-${u.id}-requirement`}>
                                        {u.kind === "wearable" ? (
                                            <>
                                                <p className="font-body text-[10px] text-warm-500 leading-snug">🔒 whichever comes first:</p>
                                                <PathList paths={u.paths(settingsWithDays)} testId={`mochi-unlock-${u.id}-paths`} />
                                            </>
                                        ) : (
                                            <>
                                                <p className="font-body text-[10px] text-warm-500 leading-snug">
                                                    🔒 {u.requirement}
                                                </p>
                                                <p className="font-body text-[10px] text-warm-500/70 mt-0.5">
                                                    {Math.min(curr, u.target)} / {u.target} {u.unit}
                                                </p>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </UnlockPreviewCard>
                    );
                })}
            </div>

            {/* Sunglasses equip toggle — only visible once unlocked */}
            {sunglassesUnlocked && (
                <ToggleRow
                    icon={<Glasses size={18} className="text-warm-900 shrink-0" />}
                    label="Sunglasses"
                    onLine="On — Mochi looks cool."
                    offLine="Off — Mochi’s eyes are free."
                    on={sunglassesEquipped}
                    onToggle={() => onUpdate({ sunglasses_equipped: !sunglassesEquipped })}
                    testId="settings-sunglasses-toggle"
                />
            )}

            {/* Sakura pin equip toggle — visible once the pin is unlocked. */}
            {pinUnlocked && (
                <ToggleRow
                    icon={<Flower2 size={18} className="text-[#E78BA8] shrink-0" />}
                    label="Sakura pin"
                    onLine="On — a little flower behind her ear 🌸"
                    offLine="Off — tucked away for now."
                    on={pinEquipped}
                    onToggle={() => onUpdate({ pin_equipped: !pinEquipped })}
                    testId="settings-pin-toggle"
                />
            )}

            {/* Milestone accessory toggles.
                Crown — PERMANENT once unlocked at Day 100.
                Birthday hat — temporary (Day 365 window) OR permanent once
                `hat_equipped` is set (owner unlock-all). Hat sits over the
                crown, so toggling it off reveals the crown underneath. */}
            {crownUnlocked && (
                <ToggleRow
                    icon={<Crown size={18} className="text-mustard shrink-0" />}
                    label="Gold crown"
                    onLine="On — yours forever 👑"
                    offLine="Off — kept safe in the closet."
                    on={crownEquipped}
                    onToggle={() => onUpdate({ crown_equipped: !crownEquipped })}
                    color="mustard"
                    testId="settings-crown-toggle"
                />
            )}
            {(hatUnlocked || hatActive || hatPermanent || hatOccasion) && (
                <ToggleRow
                    icon={<Crown size={18} className="text-mustard shrink-0" />}
                    label="Birthday hat"
                    onLine={!hatUnlocked && !hatPermanent && hatOccasion === "monthiversary"
                        ? "On — borrowed for our month-iversary 🎂 (today only)"
                        : "On — Mochi wears it proudly (sits over the crown)."}
                    offLine="Off — crown shows instead."
                    on={hatVisible}
                    onToggle={() => onUpdate({ accessory_visible: !hatVisible })}
                    color="mustard"
                    testId="settings-accessory-toggle"
                />
            )}

            {/* Real-birthday path for the hat: Mochi wears it on your day and
                keeps it unlocked after the first one. */}
            <BirthdayPicker
                value={settings.birthday || ""}
                onSave={(birthday) => onUpdate({ birthday })}
                hatUnlocked={hatUnlocked}
            />

            {/* Owner-only cheat — equip every cosmetic at once. */}
            {isAdmin && (
                <button
                    onClick={handleUnlockAll}
                    disabled={unlocking}
                    className="mt-3 w-full flex items-center justify-center gap-2 rounded-2xl bg-warm-900 px-3 py-3 text-cream font-display text-sm transition-all hover:bg-warm-900/90 disabled:opacity-60"
                    data-testid="settings-unlock-all-btn"
                    aria-busy={unlocking}
                >
                    <Wand2 size={16} />
                    {unlocking ? "Unlocking…" : "Unlock everything (owner)"}
                </button>
            )}

            {/* Cover pass indicator — surfaces once the mechanic has happened
                at least once OR the pass is currently available. The actual
                streak-3 eligibility gating lives at the row + offer surfaces. */}
            {(coverEverUsed || coverAvailable) && (
                <div
                    className="mt-3 rounded-2xl bg-cream/90 px-3 py-2.5 flex items-center gap-2.5"
                    data-testid="cover-pass-indicator"
                >
                    <span
                        className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-base ${
                            coverAvailable ? "bg-mustard-soft text-mustard" : "bg-warm-300/40 text-warm-500"
                        }`}
                        aria-hidden="true"
                    >
                        {coverAvailable ? "🍡" : "⭐"}
                    </span>
                    <div className="flex-1 min-w-0">
                        <p
                            className="font-display text-sm text-warm-900 leading-tight"
                            data-testid="cover-pass-status"
                        >
                            {coverAvailable
                                ? "mochi has a cover pass ready for you this week"
                                : "mochi covered for you this week — resets Monday"}
                        </p>
                        <p className="font-body text-[11px] text-warm-500 mt-0.5 italic">
                            act of friendship. shame-free.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
